<template>
  <div>
    <vx-card class="mb-4">
      <div class="mb-2">
        <h4 class="mb-0">{{ isEditMode ? model.name || $t('subscription_plan.title') : $t('catalog.plan.new_title') }}</h4>
        <small class="text-muted">{{ $t('catalog.plan.subtitle') }}</small>
      </div>

      <wizard-shell
        :steps="steps"
        :current="currentStep"
        :done-keys="doneKeys"
        :free-navigation="isEditMode"
        :next-disabled="nextDisabled"
        :busy="busy"
        @prev="goPrev"
        @next="goNext"
        @goto="goToStep">

        <!-- ============================== PASSO 1: SOBRE O PLANO -->
        <div v-show="currentStep === 'sobre'">
          <validation-observer ref="aboutValidator" #default="{valid}">
            <b-form @submit.prevent>
              <div class="vx-row">
                <div class="vx-col sm:w-7/12 w-full">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('subscription_plan.name')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('subscription_plan.name')" label-for="name">
                      <b-form-input
                        id="name"
                        type="text"
                        class="required"
                        v-model="model.name"
                        :placeholder="$t('catalog.plan.name_placeholder')"
                        :state="getValidationState(validationContext)"/>
                      <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-5/12 w-full">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('responsible_entity')"
                    rules="required"
                    slim>
                    <select-suggestion
                      id="legal_entity_id"
                      column="company_name,trading_name,cnpj"
                      model="LegalEntity"
                      v-model="legalEntity"
                      :label="$t('responsible_entity')"
                      :appendClearOption="false"
                      :placeholderText="pessoaJuridicaLocked ? '' : 'Digite o nome do estabelecimento'"
                      :itemTextFormat="legalEntityItemTextFormat"
                      :isDisabled="pessoaJuridicaLocked"
                      :required="true"/>
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </validation-provider>
                </div>
              </div>

              <validation-provider
                #default="validationContext"
                :name="$t('subscription_plan.description')"
                rules="max:500"
                slim>
                <b-form-group :label="$t('subscription_plan.description')" label-for="description" class="mt-1">
                  <b-form-textarea
                    id="description"
                    v-model="model.description"
                    rows="3"
                    max-rows="6"
                    :state="getValidationState(validationContext)"/>
                  <small class="text-muted d-block">{{ $t('catalog.plan.description_hint') }}</small>
                  <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <b-alert :show="model.status === 'IN'" variant="warning" class="p-1 mt-1 mb-0">
                <small>{{ $t('catalog.plan.inactive_legacy') }}</small>
              </b-alert>

              <div v-if="!valid" class="mt-1">
                <small class="text-warning">{{ $t('catalog.wizard.fill_required') }}</small>
              </div>
            </b-form>
          </validation-observer>
        </div>

        <!-- ============================== PASSO 2: REGRAS -->
        <div v-show="currentStep === 'regras'">
          <validation-observer ref="rulesValidator" #default="{valid}">
            <b-form @submit.prevent>
              <h6 class="mb-50">{{ $t('subscription_plan.section_billing') }}</h6>
              <div class="subscription-plan-interval-billing-row">
                <div class="subscription-plan-interval-block">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('subscription_plan.interval')"
                    rules="required"
                    slim>
                    <b-form-group :label="$t('subscription_plan.interval')" label-for="interval" label-class="required">
                      <b-form-radio-group
                        id="interval"
                        v-model="model.interval"
                        :options="intervalOptions"
                        stacked
                        value-field="value"
                        text-field="label"
                        :state="getValidationState(validationContext)"/>
                      <small class="text-danger d-block">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="subscription-plan-billing-block">
                  <b-form-group
                    class="flex-grow mb-0"
                    :label="$t('subscription_plan.billing_cycles')"
                    label-for="billing_cycles">
                    <small class="text-muted d-block mb-1">{{ $t('subscription_plan.billing_cycles_help') }}</small>
                    <b-form-input
                      id="billing_cycles"
                      v-model="model.billing_cycles"
                      type="number"
                      min="1"
                      step="1"
                      :placeholder="$t('subscription_plan.billing_unlimited')"/>
                  </b-form-group>
                </div>
              </div>

              <hr class="my-2"/>

              <h6 class="mb-50">{{ $t('subscription_plan.section_entitlement') }}</h6>
              <b-form-group :label="$t('subscription_plan.commitment_type')">
                <choice-cards
                  :options="commitmentCards"
                  :value="model.commitment_type"
                  :disabled="lockedFieldsFrozen"
                  @input="model.commitment_type = $event"/>
                <small v-if="lockedFieldsFrozen" class="text-muted d-block mt-1">{{ $t('subscription_plan.locked_by_subscribers') }}</small>
              </b-form-group>

              <!-- v-if e não :disabled: escondido, o provider se desregistra do
                   observer e o botão volta a habilitar no plano ilimitado. -->
              <div class="vx-row" v-if="hasQuota">
                <div class="vx-col sm:w-5/12 w-full">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('subscription_plan.classes_per_period')"
                    rules="required|integer|min_value:1"
                    slim>
                    <b-form-group :label="$t('subscription_plan.classes_per_period')" label-for="classes_per_period">
                      <small class="text-muted d-block mb-1">{{ $t('subscription_plan.classes_per_period_help') }}</small>
                      <b-form-input
                        id="classes_per_period"
                        class="required"
                        v-model.number="model.classes_per_period"
                        type="number"
                        min="1"
                        step="1"
                        :disabled="lockedFieldsFrozen"
                        :state="getValidationState(validationContext)"/>
                      <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-7/12 w-full">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('subscription_plan.quota_period')"
                    rules="required"
                    :immediate="true"
                    slim>
                    <b-form-group :label="$t('subscription_plan.quota_period')" label-for="quota_period">
                      <b-form-radio-group
                        id="quota_period"
                        v-model="model.quota_period"
                        :options="quotaPeriodOptions"
                        value-field="value"
                        text-field="label"
                        :disabled="lockedFieldsFrozen"
                        :state="getValidationState(validationContext)"/>
                      <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>

              <!-- Frase montada com o que já foi escolhido: é o teste mais rápido
                   de que a configuração diz o que o clube acha que disse. -->
              <div class="border rounded p-1 mb-2" v-if="entitlementSummary">
                <small class="text-muted d-block">{{ $t('subscription_plan.entitlement_preview') }}</small>
                <span class="font-medium">{{ entitlementSummary }}</span>
              </div>

              <div class="vx-row">
                <div class="vx-col sm:w-6/12 w-full">
                  <b-form-group :label="$t('subscription_plan.booking_horizon_days')" label-for="booking_horizon_days">
                    <small class="text-muted d-block mb-1">{{ $t('subscription_plan.booking_horizon_days_help') }}</small>
                    <b-input-group append="dias" style="max-width: 10rem;">
                      <b-form-input
                        id="booking_horizon_days"
                        v-model.number="model.booking_horizon_days"
                        type="number"
                        min="7"
                        max="365"
                        step="1"/>
                    </b-input-group>
                  </b-form-group>
                </div>
                <div class="vx-col sm:w-6/12 w-full">
                  <b-form-group :label="$t('subscription_plan.cancel_at_period_end')" label-for="cancel_at_period_end">
                    <small class="text-muted d-block mb-2">{{ $t('subscription_plan.cancel_at_period_end_help') }}</small>
                    <b-form-checkbox id="cancel_at_period_end" v-model="model.cancel_at_period_end" switch/>
                  </b-form-group>
                </div>
              </div>

              <hr class="my-2"/>

              <b-form-group :label="$t('subscription_plan.allow_makeup')" label-for="allow_makeup">
                <small class="text-muted d-block mb-2">{{ $t('subscription_plan.allow_makeup_help') }}</small>
                <b-form-checkbox id="allow_makeup" v-model="model.allow_makeup" switch/>
              </b-form-group>

              <div class="vx-row" v-if="model.allow_makeup">
                <div class="vx-col sm:w-6/12 w-full">
                  <b-form-group :label="$t('subscription_plan.makeup_expires_in_days')" label-for="makeup_expires_in_days">
                    <b-input-group append="dias" style="max-width: 10rem;">
                      <b-form-input
                        id="makeup_expires_in_days"
                        v-model.number="model.makeup_expires_in_days"
                        type="number"
                        min="1"
                        step="1"/>
                    </b-input-group>
                  </b-form-group>
                </div>
                <div class="vx-col sm:w-6/12 w-full">
                  <b-form-group :label="$t('subscription_plan.min_cancel_advance_hours')" label-for="min_cancel_advance_hours">
                    <b-input-group append="horas" style="max-width: 10rem;">
                      <b-form-input
                        id="min_cancel_advance_hours"
                        v-model.number="model.min_cancel_advance_hours"
                        type="number"
                        min="0"
                        step="1"/>
                    </b-input-group>
                  </b-form-group>
                </div>
              </div>

              <div v-if="!valid" class="mt-1">
                <small class="text-warning">{{ $t('catalog.wizard.fill_required') }}</small>
              </div>
            </b-form>
          </validation-observer>
        </div>

        <!-- ============================== PASSO 3: AULAS INCLUÍDAS -->
        <div v-show="currentStep === 'aulas'">
          <b-form-group :label="$t('subscription_plan.eligibility_scope')">
            <small class="text-muted d-block mb-1">{{ $t('subscription_plan.eligibility_scope_help') }}</small>
            <b-form-radio-group
              id="eligibility_scope"
              v-model="model.eligibility_scope"
              :options="eligibilityScopeOptions"
              stacked
              value-field="value"
              text-field="label"
              :disabled="lockedFieldsFrozen"/>
          </b-form-group>

          <b-alert :show="needsList && eligibilityCount === 0" variant="warning" class="p-1 mb-2">
            <small>{{ $t('catalog.plan.eligibility_pending') }}</small>
          </b-alert>

          <subscription-plan-eligibility
            v-if="planId && needsList"
            :id="planId"
            :scope="model.eligibility_scope"
            :key="`eligibility-${model.eligibility_scope}`"
            @changed="refreshEligibilityCount"/>
        </div>

        <!-- ============================== PASSO 4: PREÇO E FOTO -->
        <div v-show="currentStep === 'venda'">
          <b-alert :show="productLoadFailed" variant="warning" class="p-1 mb-2">
            <small>{{ $t('catalog.sale.sale_unavailable') }}</small>
          </b-alert>
          <sale-step
            v-if="productExists"
            :product-id="product.id"
            :price.sync="product.price"
            :description.sync="product.description"
            :how-works.sync="product.how_works"
            :guidelines.sync="product.guidelines"
            :files="productFiles"
            :subscription-plan="salePlanInfo"
            :register-on-gateway="productRegisterOnGateway"
            :gateway-registered="productGatewayRegistered"
            :multi-sale="productMultiSale"
            @file-uploaded="onProductFileUploaded"
            @delete-file="deleteProductFile"/>
        </div>

        <!-- ============================== PASSO 5: REVISAR E PUBLICAR -->
        <div v-show="currentStep === 'revisar'">
          <b-alert :show="model.status === 'IN'" variant="warning" class="p-1 mb-2">
            <small>{{ $t('catalog.plan.inactive_legacy') }}</small>
          </b-alert>
          <review-step
            :summary="reviewSummary"
            :price="product.price"
            :price-suffix="priceSuffix"
            :published="productPublished"
            :blockers="publishBlockers"
            :front-url="publishedFrontUrl"
            :busy="busy"
            price-step-key="venda"
            @goto="goToStep"
            @publish="publish"
            @save-draft="saveDraft"
            @unpublish="unpublish"/>
        </div>
      </wizard-shell>
    </vx-card>

    <!-- Consulta, não configuração: assinantes ficam fora do passo a passo. -->
    <vx-card :title="$t('subscription_plan.section_subscribers')" class="mb-4" v-if="planId">
      <subscription-plan-subscribers :id="planId"/>
    </vx-card>
  </div>
</template>

<script>
import {
  BAlert,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BFormTextarea,
  BFormRadioGroup,
  BFormCheckbox,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { mapGetters } from 'vuex'

import SelectSuggestion from '@/components/SelectSuggestion.vue'
import WizardShell from '@/components/catalog/WizardShell.vue'
import ChoiceCards from '@/components/catalog/ChoiceCards.vue'
import SaleStep from '@/components/catalog/SaleStep.vue'
import ReviewStep from '@/components/catalog/ReviewStep.vue'
import productBinding from '@/components/catalog/productBinding'
import { marketplaceItemUrl } from '@/components/catalog/catalog'

import SubscriptionPlanService from '@/services/api/SubscriptionPlanService'
import SubscriptionPlanEligibility from './Eligibility.vue'
import SubscriptionPlanSubscribers from './Subscribers.vue'

const STEP_KEYS = ['sobre', 'regras', 'aulas', 'venda', 'revisar']

const PERIOD_NOUNS = {
  week: 'por semana',
  month: 'por mês',
  cycle: 'por ciclo de cobrança',
}

/** Sufixo humano do preço por periodicidade — "R$ 200,00 por mês". */
const INTERVAL_PRICE_SUFFIX = {
  weekly: 'por semana',
  biweekly: 'a cada 15 dias',
  monthly: 'por mês',
  bimonthly: 'a cada 2 meses',
  semiannual: 'por semestre',
  annual: 'por ano',
}

/**
 * Cadastro de plano em passo a passo, com o produto embutido.
 *
 * Diferenças estruturais deste wizard:
 * - o backend exige `interval` já no primeiro POST, então o plano só é criado
 *   ao concluir o passo "Regras" (o passo 1 valida localmente e segue);
 * - o status AC/IN do plano sumiu da tela — plano novo nasce AC e publicar um
 *   legado IN reativa junto (aviso visível). "Despublicar" age no anúncio
 *   (produto → DI), nunca no plano;
 * - "Onde é vendido" e "Criar produto para este plano" morreram: o anúncio é
 *   criado e publicado aqui dentro (1 plano ↔ 1 anúncio, trava do front).
 */
export default {
  mixins: [productBinding],
  components: {
    BAlert,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BFormTextarea,
    BFormRadioGroup,
    BFormCheckbox,
    BFormInvalidFeedback,
    ValidationObserver,
    ValidationProvider,
    SelectSuggestion,
    WizardShell,
    ChoiceCards,
    SaleStep,
    ReviewStep,
    SubscriptionPlanEligibility,
    SubscriptionPlanSubscribers,
  },

  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },

  data: () => ({
    service: null,
    currentStep: 'sobre',
    busy: false,
    /** Id do plano recém-criado, antes de a prop refletir o replace create→edit. */
    internalId: null,
    eligibilityCount: null,
    hasLiveSubscribers: false,
    model: {
      name: '',
      status: 'AC',
      description: '',
      interval: null,
      billing_cycles: null,
      commitment_type: 'FLX',
      classes_per_period: null,
      quota_period: 'week',
      booking_horizon_days: 56,
      cancel_at_period_end: true,
      allow_makeup: false,
      makeup_expires_in_days: null,
      min_cancel_advance_hours: null,
      eligibility_scope: 'ALL',
    },
    legalEntity: null,
    intervalOptions: [],
  }),

  computed: {
    ...mapGetters('dataAccess', ['activeEntity', 'entities', 'loaded', 'globalInstitutionLocked']),
    planId() {
      if (this.internalId) return this.internalId
      return this.id === null || this.id === undefined || this.id === '' ? null : Number(this.id)
    },
    isEditMode() {
      return this.planId !== null
    },
    pessoaJuridicaLocked() {
      return this.globalInstitutionLocked || (Array.isArray(this.entities) && this.entities.length === 1)
    },
    /** Com assinante vivo o backend recusa mudar a estrutura — trava na tela. */
    lockedFieldsFrozen() {
      return this.hasLiveSubscribers
    },
    steps() {
      return [
        { key: 'sobre', title: this.$t('catalog.plan.step_about'), hint: this.$t('catalog.plan.step_about_hint') },
        { key: 'regras', title: this.$t('catalog.plan.step_rules'), hint: this.$t('catalog.plan.step_rules_hint') },
        { key: 'aulas', title: this.$t('catalog.plan.step_lessons'), hint: this.$t('catalog.plan.step_lessons_hint') },
        { key: 'venda', title: this.$t('catalog.sale.step_title'), hint: this.$t('catalog.sale.step_hint') },
        { key: 'revisar', title: this.$t('catalog.review.step_title'), hint: this.$t('catalog.review.step_hint') },
      ]
    },
    doneKeys() {
      const done = []
      if (this.model.name) done.push('sobre')
      if (this.planId) done.push('regras', 'aulas')
      if (this.productExists) done.push('venda')
      return done
    },
    nextDisabled() {
      return false
    },
    hasQuota() {
      return this.model.commitment_type === 'FIX' || this.model.commitment_type === 'FLX'
    },
    needsList() {
      return this.model.eligibility_scope === 'LES' || this.model.eligibility_scope === 'MOD'
    },
    intervalLabel() {
      const option = this.intervalOptions.find(item => item.value === this.model.interval)
      return option ? option.label : ''
    },
    commitmentCards() {
      return [
        {
          value: 'FIX',
          title: 'Horários fixos',
          description: 'O aluno escolhe os horários na contratação e a vaga fica reservada toda semana.',
          example: 'Ex.: sempre terça e quinta às 7h',
          icon: 'LockIcon',
        },
        {
          value: 'FLX',
          title: 'Livre com cota',
          description: 'O aluno agenda quando quiser, dentro de um limite de aulas por período.',
          example: 'Ex.: 8 aulas por mês, em qualquer horário livre',
          icon: 'SlidersIcon',
        },
        {
          value: 'UNL',
          title: 'Ilimitado',
          description: 'Sem limite de aulas — o aluno agenda quantas quiser nos horários livres.',
          example: 'Ex.: passe livre',
          icon: 'ZapIcon',
        },
      ]
    },
    quotaPeriodOptions() {
      const labels = this.$t('subscription_plan.quota_period_labels')
      return [
        { value: 'week', label: labels.week },
        { value: 'month', label: labels.month },
        { value: 'cycle', label: labels.cycle },
      ]
    },
    eligibilityScopeOptions() {
      const labels = this.$t('subscription_plan.eligibility_scope_labels')
      return [
        { value: 'ALL', label: labels.ALL },
        { value: 'MOD', label: labels.MOD },
        { value: 'LES', label: labels.LES },
      ]
    },
    entitlementSummary() {
      if (!this.hasQuota) {
        return this.model.commitment_type === 'UNL'
          ? `Aulas ilimitadas, com cobrança ${(this.intervalLabel || '').toLowerCase()}.`
          : ''
      }
      const quota = Number(this.model.classes_per_period)
      if (!quota) return ''
      const noun = quota === 1 ? 'aula' : 'aulas'
      const period = PERIOD_NOUNS[this.model.quota_period] || ''
      const cobranca = this.intervalLabel ? `, com cobrança ${this.intervalLabel.toLowerCase()}` : ''
      const escolha = this.model.commitment_type === 'FIX'
        ? ' O aluno escolhe os horários na contratação e a vaga fica reservada.'
        : ' O aluno agenda quando quiser, dentro do limite.'
      return `${quota} ${noun} ${period}${cobranca}.${escolha}`
    },
    /**
     * Info de assinatura para o SaleStep: preferindo a que veio do produto
     * (tem os flags de gateway), com fallback local logo após o ensureProduct.
     */
    salePlanInfo() {
      if (this.productSubscriptionPlan) return this.productSubscriptionPlan
      return { name: this.model.name, interval_label: this.intervalLabel }
    },
    priceSuffix() {
      return INTERVAL_PRICE_SUFFIX[this.model.interval] || null
    },
    scopeLabel() {
      const option = this.eligibilityScopeOptions.find(item => item.value === this.model.eligibility_scope)
      return option ? option.label : ''
    },
    publishBlockers() {
      const blockers = []
      if (!this.productExists) {
        blockers.push({ text: this.$t('catalog.lesson.blocker_sale_step'), stepKey: 'venda' })
      }
      if (this.productExists && (this.product.price === null || this.product.price === '')) {
        blockers.push({ text: this.$t('catalog.plan.blocker_price'), stepKey: 'venda' })
      }
      if (this.needsList && this.eligibilityCount === 0) {
        blockers.push({ text: this.$t('catalog.plan.blocker_eligibility'), stepKey: 'aulas' })
      }
      if (this.productLoadFailed) {
        blockers.push({ text: this.$t('catalog.sale.sale_unavailable') })
      }
      return blockers
    },
    publishedFrontUrl() {
      return this.productPublished ? marketplaceItemUrl('SPL', this.product.id) : null
    },
    reviewSummary() {
      const aboutRows = [
        { label: this.$t('subscription_plan.name'), value: this.model.name },
        { label: this.$t('responsible_entity'), value: this.legalEntity && (this.legalEntity.trading_name || this.legalEntity.company_name) },
      ]

      const billingRows = [
        { label: this.$t('catalog.plan.review_interval'), value: this.intervalLabel },
        {
          label: this.$t('catalog.plan.review_cycles'),
          value: this.model.billing_cycles ? this.model.billing_cycles : this.$t('catalog.plan.review_cycles_unlimited'),
        },
        { label: this.$t('catalog.plan.review_entitlement'), value: this.entitlementSummary },
        { label: this.$t('subscription_plan.allow_makeup'), value: this.model.allow_makeup ? 'Sim' : 'Não' },
      ]

      const eligibilityRows = [{
        label: this.scopeLabel || this.$t('catalog.plan.review_eligibility'),
        value: this.model.eligibility_scope === 'ALL'
          ? this.$t('catalog.plan.review_eligibility_all')
          : (this.eligibilityCount === null
            ? '…'
            : this.$tc('catalog.plan.review_eligibility_count', this.eligibilityCount || 0, { count: this.eligibilityCount || 0 })),
        missing: this.needsList && this.eligibilityCount === 0,
      }]

      return [
        { key: 'sobre', title: this.$t('catalog.plan.step_about'), icon: 'FileTextIcon', stepKey: 'sobre', rows: aboutRows },
        { key: 'regras', title: this.$t('catalog.plan.step_rules'), icon: 'SettingsIcon', stepKey: 'regras', rows: billingRows },
        { key: 'aulas', title: this.$t('catalog.plan.step_lessons'), icon: 'BookOpenIcon', stepKey: 'aulas', rows: eligibilityRows },
      ]
    },
  },

  watch: {
    '$route.query.step'(value) {
      const step = STEP_KEYS.includes(value) ? value : 'sobre'
      if (step !== this.currentStep) {
        this.enterStep(step)
      }
    },
  },

  methods: {
    legalEntityItemTextFormat(model) {
      const trading = model?.trading_name || ''
      const company = model?.company_name || ''
      const label = trading && company ? `${trading} - ${company}` : (trading || company)
      return label || model?.name || `ID: ${model?.id}`
    },
    prefillLegalEntity() {
      if (this.legalEntity || this.planId) {
        return
      }
      if (this.activeEntity) {
        this.legalEntity = { id: this.activeEntity.id, trading_name: this.activeEntity.name || '', company_name: '' }
        return
      }
      if (Array.isArray(this.entities) && this.entities.length === 1) {
        const [entity] = this.entities
        this.legalEntity = { id: entity.id, trading_name: entity.trading_name || entity.name || '', company_name: entity.company_name || '' }
      }
    },
    getValidationState({ invalid, validated }) {
      const valid = !invalid
      return !validated ? null : valid
    },

    // ------------------------------------------------------------------
    // Navegação
    // ------------------------------------------------------------------
    stepIndex(key) {
      return STEP_KEYS.indexOf(key)
    },
    syncStepQuery(step) {
      if (this.$route.query.step !== step) {
        this.$router.replace({ query: { ...this.$route.query, step } }).catch(() => {})
      }
    },
    async goNext() {
      const index = this.stepIndex(this.currentStep)
      const next = STEP_KEYS[index + 1]
      if (!next) return
      const left = await this.leaveStep(this.currentStep)
      if (!left) return
      await this.enterStep(next)
    },
    goPrev() {
      const index = this.stepIndex(this.currentStep)
      const prev = STEP_KEYS[index - 1]
      if (prev) this.goToStep(prev)
    },
    async goToStep(step) {
      if (step === this.currentStep) return
      const left = await this.leaveStep(this.currentStep)
      if (!left) return
      await this.enterStep(step)
    },
    async leaveStep(step) {
      if (step === 'sobre') {
        const valid = await this.$refs.aboutValidator.validate()
        if (!valid) {
          this.notifyWarning(this.$vs, this.$t('catalog.wizard.fill_required'))
          return false
        }
        // Sem id ainda não há o que gravar: o backend exige `interval`, que
        // mora no passo seguinte. O create acontece ao concluir "Regras".
        return this.planId ? this.savePlanCore() : true
      }
      if (step === 'regras') {
        const valid = await this.$refs.rulesValidator.validate()
        if (!valid) {
          this.notifyWarning(this.$vs, this.$t('catalog.wizard.fill_required'))
          return false
        }
        return this.savePlanCore()
      }
      if (step === 'aulas') {
        return this.savePlanCore()
      }
      return true
    },
    async enterStep(step) {
      if (!this.planId && !['sobre', 'regras'].includes(step)) {
        step = this.model.name ? 'regras' : 'sobre'
      }
      // Reentrada (watcher da query + goNext chegam juntos no create→edit).
      if (step === this.currentStep && this.$route.query.step === step) {
        return
      }
      if (step === 'venda' && this.planId) {
        const ready = await this.ensureSale()
        if (!ready) return
      }
      if (step === 'aulas' && this.planId) {
        this.refreshEligibilityCount()
      }
      if (step === 'revisar' && this.planId) {
        this.refreshEligibilityCount()
        if (!this.productExists && !this.productLoadFailed) {
          await this.ensureSale()
        }
      }
      this.currentStep = step
      this.syncStepQuery(step)
    },

    // ------------------------------------------------------------------
    // Persistência
    // ------------------------------------------------------------------
    payload(statusOverride = null) {
      return {
        id: this.planId,
        name: this.model.name,
        status: statusOverride || this.model.status || 'AC',
        legal_entity_id: this.legalEntity ? this.legalEntity.id : null,
        description: this.model.description,
        interval: this.model.interval,
        billing_cycles:
          this.model.billing_cycles === '' || this.model.billing_cycles === null
            ? null
            : Number(this.model.billing_cycles),
        commitment_type: this.model.commitment_type,
        // Sem cota os dois vão nulos, senão um plano trocado para ilimitado
        // continuaria carregando "3 por semana" no banco.
        classes_per_period: this.hasQuota && this.model.classes_per_period
          ? Number(this.model.classes_per_period)
          : null,
        quota_period: this.hasQuota ? this.model.quota_period : null,
        eligibility_scope: this.model.eligibility_scope,
        booking_horizon_days: this.model.booking_horizon_days || null,
        cancel_at_period_end: !!this.model.cancel_at_period_end,
        allow_makeup: !!this.model.allow_makeup,
        makeup_expires_in_days: this.model.allow_makeup ? (this.model.makeup_expires_in_days || null) : null,
        min_cancel_advance_hours: this.model.min_cancel_advance_hours ?? null,
      }
    },
    async savePlanCore(statusOverride = null) {
      this.busy = true
      this.$vs.loading()
      try {
        const wasCreate = !this.planId
        const data = await this.service.createOrUpdate(this.payload(statusOverride))
        this.model.status = data.status || this.model.status
        if (data.gateway_registration_warning) {
          this.notifyWarning(this.$vs, data.gateway_registration_warning, 10000)
        }
        if (wasCreate) {
          this.internalId = data.id
          // Aguardado: navegação dupla sob o guard assíncrono cancela as duas
          // (ver saveLessonCore do formulário de aula).
          await this.$router.replace(`/subscription_plans/${data.id}/edit?step=aulas`).catch(() => {})
        }
        return true
      } catch (error) {
        this.showErrors(this.$refs.rulesValidator || this.$refs.aboutValidator, error, this)
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    async ensureSale() {
      if (!this.planId || !this.legalEntity) {
        return false
      }
      if (this.productExists) {
        return true
      }
      // O vínculo produto↔plano exige plano ativo; um legado IN reativa aqui,
      // com o aviso `inactive_legacy` visível nos passos anteriores.
      if (this.model.status !== 'AC') {
        const reactivated = await this.savePlanCore('AC')
        if (!reactivated) return false
      }
      this.busy = true
      this.$vs.loading()
      try {
        await this.ensureProduct({
          itemType: 'SPL',
          itemId: this.planId,
          itemName: this.model.name,
          legalEntityId: this.legalEntity.id,
        })
        // Recarrega para trazer subscription_plan + flags de gateway.
        await this.loadLinkedProduct('SPL', this.planId, this.model.name)
        this.productLoadFailed = false
        return true
      } catch (error) {
        if (error && error.message === 'sale-unavailable') {
          this.productLoadFailed = true
          return false
        }
        this.notifyErrors(this.$vs, error)
        this.productLoadFailed = true
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    async refreshEligibilityCount() {
      if (!this.planId || !this.needsList) {
        this.eligibilityCount = null
        return
      }
      try {
        const list = this.model.eligibility_scope === 'LES'
          ? await this.service.getLessons(this.planId)
          : await this.service.getModalities(this.planId)
        this.eligibilityCount = Array.isArray(list) ? list.length : 0
      } catch (error) {
        this.eligibilityCount = null
      }
    },

    // ------------------------------------------------------------------
    // Publicação
    // ------------------------------------------------------------------
    async publish() {
      if (this.productNeedsPriceConfirmation()) {
        this.confirmProductPriceChange(() => this.publish())
        return
      }
      await this.persistAll('RE', this.$t('catalog.review.published_success'))
    },
    async saveDraft() {
      const status = this.product.status && this.product.status !== 'RE' ? this.product.status : 'CR'
      await this.persistAll(status, this.$t('catalog.review.draft_saved'))
    },
    async unpublish() {
      await this.persistAll('DI', this.$t('catalog.review.unpublished_success'))
    },
    async persistAll(productStatus, successMessage) {
      this.busy = true
      this.$vs.loading()
      try {
        // Publicar reativa plano legado IN; despublicar NÃO inativa o plano —
        // o anúncio some da vitrine e o plano segue cobrando quem já assinou.
        const planStatus = productStatus === 'RE' ? 'AC' : (this.model.status || 'AC')
        const data = await this.service.createOrUpdate(this.payload(planStatus))
        this.model.status = data.status || planStatus
        if (data.gateway_registration_warning) {
          this.notifyWarning(this.$vs, data.gateway_registration_warning, 10000)
        }
        await this.saveProductAs(productStatus, {
          itemName: this.model.name,
          legalEntityId: this.legalEntity ? this.legalEntity.id : null,
        })
        this.notifySuccess(this.$vs, successMessage)
      } catch (error) {
        this.notifyErrors(this.$vs, error)
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },

    // ------------------------------------------------------------------
    // Carga
    // ------------------------------------------------------------------
    async loadPlanRecord(id) {
      const response = await this.service.read(id)
      Object.keys(this.model).forEach(key => {
        if (response[key] !== undefined && response[key] !== null) this.model[key] = response[key]
      })
      this.model.status = (response.status && String(response.status)) || 'AC'
      // Booleanos chegam como 0/1 em alguns caminhos do backend.
      this.model.allow_makeup = Boolean(response.allow_makeup)
      this.model.cancel_at_period_end = Boolean(response.cancel_at_period_end)
      this.hasLiveSubscribers = Boolean(response.has_live_subscriptions)

      if (response.legal_entity) {
        this.legalEntity = response.legal_entity
      } else if (response.legal_entity_id) {
        const match = (this.entities || []).find(entity => entity.id === response.legal_entity_id)
        this.legalEntity = match || { id: response.legal_entity_id, trading_name: '', company_name: '' }
      }
      return response
    },
    defaultIntervalOptions() {
      const values = ['weekly', 'biweekly', 'monthly', 'bimonthly', 'semiannual', 'annual']
      return values.map(value => ({
        value,
        label: this.$t(`subscription_plan.interval_labels.${value}`),
      }))
    },
    async loadOptionLists() {
      try {
        const intervals = await this.service.findAllIntervals()
        this.intervalOptions = Array.isArray(intervals) && intervals.length ? intervals : this.defaultIntervalOptions()
      } catch (e) {
        this.intervalOptions = this.defaultIntervalOptions()
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    const stayingInForm = (to.name === 'subscription-plan-edit' || to.name === 'subscription-plan-create')
      && String(to.params.id || '') === String(this.planId || '')
    const priceDirty = this.productExists
      && this.productOriginalPrice !== null
      && Number(this.product.price) !== Number(this.productOriginalPrice)
    if (!stayingInForm && priceDirty) {
      if (!window.confirm(this.$t('catalog.wizard.unsaved_leave_confirm'))) {
        next(false)
        return
      }
    }
    next()
  },

  beforeMount() {
    this.service = SubscriptionPlanService.build(this.$vs)
  },

  async mounted() {
    this.$vs.loading()
    try {
      if (!this.loaded) {
        await this.$store.dispatch('dataAccess/setupFromLogin')
      }
      this.prefillLegalEntity()
      await this.loadOptionLists()
      if (this.planId) {
        await this.loadPlanRecord(this.planId)
        await this.loadLinkedProduct('SPL', this.planId, this.model.name)
      }
    } finally {
      this.$vs.loading.close()
    }

    const requested = this.$route.query.step
    const initial = STEP_KEYS.includes(requested) ? requested : 'sobre'
    await this.enterStep(initial)
  },
}
</script>

<style lang="scss">
  /* Intervalo + ciclos encostados: intervalo não expande (não empurra ciclos para a direita) */
  .subscription-plan-interval-billing-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-start;
    column-gap: 1.25rem;
    row-gap: 0.5rem;
    margin-top: 0.25rem;
  }

  .subscription-plan-interval-block {
    flex: 0 1 auto;
    width: auto;
    max-width: 100%;
  }

  .subscription-plan-billing-block {
    flex: 0 0 auto;
    width: auto;
    max-width: 11rem;
    min-width: 8.5rem;

    @media (max-width: 575px) {
      flex: 1 1 100%;
      max-width: 100%;
      min-width: 0;
    }
  }
</style>
